import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  EngagementStatus,
  IEngagement
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { ActionEngagementDetails } from "../components/ActionEngagementDetails";
import { useMutationUpdateEngagement } from "../../../query/engagements/mutations/useMutationUpdateEngagement";

export interface RestoreEngagementTypeModalProps {
  engagement: IEngagement;
  onClose: () => void;
}

export const RestoreEngagementTypeModal: React.FC<
  RestoreEngagementTypeModalProps
> = ({ engagement, onClose }) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagementType, updateEngagementTypeMeta] =
    useMutationUpdateEngagement();

  const handleRestore = async () => {
    await updateEngagementType({
      engagementId: engagement.engagementId,
      status: EngagementStatus.Active
    });

    openSnackbar({
      theme: "dark",
      type: "success",
      message: "Engagement restored"
    });
    onClose();
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you wish to restore this engagement?
      </WSText.ParagraphSm>
      <ActionEngagementDetails engagement={engagement} />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring engagement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            Any archived assignments associated with this engagement will remain
            archived and can be restored individually.
          </li>
          <li>Changes can be made to this engagement.</li>
          <li>Invoices can be created for this engagement.</li>
        </WSElement>
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementTypeMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Engagement}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore engagement",
            loading: updateEngagementTypeMeta.isLoading,
            onAsyncClick: handleRestore,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useRestoreEngagementTypeModal = () => {
  return useWSModal(RestoreEngagementTypeModal, {
    title: "Restore engagement",
    size: "M"
  });
};
